/**
 * web/src/components/layout/ReconnectingBanner.tsx
 * Inline amber banner above the topbar when WS has been reconnecting (or
 * fully disconnected) for more than 3 seconds (CONTEXT.md D-2.3 — the
 * 3-second debounce prevents the banner from flashing on momentary
 * blips when StrictMode tears down + reopens the WS in dev or when a
 * tab regains focus and the underlying socket renegotiates).
 *
 * a11y: role="status" + aria-live="polite" so screen readers announce
 * the connection drop without interrupting the current task.
 *
 * Hidden when WS is connected (the sidebar dot already conveys steady
 * state). Re-arms the 3s timer if the connection bounces between
 * reconnecting and disconnected without ever settling on connected.
 */
import { useEffect, useState } from 'react';
import { useWS } from '../../hooks/useWS';
import { Icon } from '../../lib/icons';

const SHOW_DELAY_MS = 3000;

/**
 * Renders the reconnecting banner. Reads the live WS status via useWS();
 * mounts inside AppShell above the Topbar.
 *
 * @sideEffects schedules a setTimeout while reconnecting/disconnected;
 *              cleans up on status change or unmount
 * @idempotent yes — rapid status flips reset the timer, so the banner
 *             stays hidden until the WS has actually been down for
 *             SHOW_DELAY_MS milliseconds
 */
export function ReconnectingBanner() {
  const ws = useWS();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (ws !== 'reconnecting' && ws !== 'disconnected') {
      // Connected — hide immediately.
      setShow(false);
      return;
    }
    // Reset and re-arm whenever we transition between disconnected /
    // reconnecting; the user shouldn't see the banner if the network
    // recovers within 3 seconds.
    setShow(false);
    const t = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [ws]);

  if (!show) return null;

  const message =
    ws === 'reconnecting'
      ? 'Reconnecting to the live updates feed…'
      : 'Disconnected from the live updates feed. Refresh if this persists.';

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-waiting-soft border-b border-waiting-border text-waiting px-9 sm:px-4 py-2 text-[13px] flex items-center gap-2"
    >
      <Icon name="alert" size={14} />
      <span>{message}</span>
    </div>
  );
}
